import React, { useState, useEffect } from 'react';
import { Button, Input, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import SideModal from '../../components/Common/SideModal';
import { personnelAPI } from '../../helpers/api';
import { useIsAdmin } from '../../helpers/hooks';

interface Personnel {
  id: string;
  firstName: string;
  lastName: string;
  title: string | null;
  phone: string | null;
}

interface ProjectPersonnel {
  id: string;
  personnel: Personnel;
}

interface Project {
  id: string;
  name: string;
  code: string;
  city: { name: string };
}

interface ProjectPersonnelModalProps {
  isOpen: boolean;
  toggle: () => void;
  project: Project | null;
  onUpdate?: () => void;
}

const ProjectPersonnelModal: React.FC<ProjectPersonnelModalProps> = ({
  isOpen,
  toggle,
  project,
  onUpdate,
}) => {
  const isAdmin = useIsAdmin();
  const [isExpanded, setIsExpanded] = useState(false);
  const [projectPersonnel, setProjectPersonnel] = useState<ProjectPersonnel[]>([]);
  const [unassignedPersonnel, setUnassignedPersonnel] = useState<Personnel[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [adding, setAdding] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && project) {
      fetchData();
    }
  }, [isOpen, project]);

  const fetchData = async () => {
    if (!project) return;

    try {
      setLoading(true);
      const [projectRes, unassignedRes] = await Promise.all([
        personnelAPI.getProjectPersonnel(project.id),
        personnelAPI.getUnassignedForProject(project.id),
      ]);
      setProjectPersonnel(projectRes.data);
      setUnassignedPersonnel(unassignedRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Personeller yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (personnelId: string) => {
    if (!project) return;

    try {
      setAdding(personnelId);
      await personnelAPI.assignToProject(personnelId, project.id);
      toast.success('Personel projeye atandı');
      fetchData();
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Atama başarısız');
    } finally {
      setAdding(null);
    }
  };

  const handleRemove = async (personnelId: string) => {
    if (!project) return;
    if (!window.confirm('Personeli projeden çıkarmak istediğinize emin misiniz? Bu işlem personeli tüm şantiyelerden de çıkaracaktır.')) return;

    try {
      setRemoving(personnelId);
      await personnelAPI.removeFromProject(personnelId, project.id);
      toast.success('Personel projeden çıkarıldı');
      fetchData();
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İşlem başarısız');
    } finally {
      setRemoving(null);
    }
  };

  const filteredUnassigned = unassignedPersonnel.filter(
    (p) =>
      `${p.firstName} ${p.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleExpand = () => setIsExpanded(!isExpanded);

  if (!project) return null;

  const headerActions = (
    <>
      {isAdmin && (
        <button
          type="button"
          className="side-modal-expand-btn me-1"
          onClick={() => setShowAddPanel(!showAddPanel)}
          title={showAddPanel ? 'Listeye Dön' : 'Personel Ekle'}
          style={{ color: showAddPanel ? '#f06548' : '#0ab39c' }}
        >
          <i className={showAddPanel ? 'ri-arrow-left-line' : 'ri-user-add-line'}></i>
        </button>
      )}
      <button
        type="button"
        className="side-modal-expand-btn"
        onClick={toggleExpand}
        title={isExpanded ? 'Küçült' : 'Genişlet'}
      >
        <i className={isExpanded ? 'ri-contract-left-right-line' : 'ri-expand-left-right-line'}></i>
      </button>
    </>
  );

  // Group by title for summary
  const personnelByTitle = projectPersonnel.reduce((acc, pp) => {
    const title = pp.personnel.title || 'Diğer';
    if (!acc[title]) acc[title] = 0;
    acc[title]++;
    return acc;
  }, {} as Record<string, number>);

  return (
    <SideModal
      isOpen={isOpen}
      toggle={toggle}
      title="Proje Personeli"
      size={isExpanded ? 'expanded' : 'lg'}
      headerActions={headerActions}
      hideBackdrop
    >
      <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
        {/* Header Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }} className="rounded-3 p-3 mb-3">
          <h4 className="mb-1" style={{ fontWeight: 600 }}>
            {project.name}
          </h4>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            {project.city.name} • {project.code}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
          </div>
        ) : showAddPanel ? (
          /* Add Personnel Panel */
          <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #405189' }} className="rounded-3 p-3 mb-3">
            <div className="text-muted small mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Projeye Personel Ekle
            </div>
            <Input
              type="text"
              placeholder="İsim veya ünvan ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mb-3"
            />
            {filteredUnassigned.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted mb-0">
                  {searchTerm
                    ? 'Aramayla eşleşen personel bulunamadı'
                    : 'Atanabilecek personel bulunmuyor'}
                </p>
              </div>
            ) : (
              <div style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }} className="rounded-3 overflow-hidden">
                {filteredUnassigned.map((p, idx) => (
                  <div
                    key={p.id}
                    className="d-flex align-items-center justify-content-between px-3 py-2"
                    style={{ borderBottom: idx < filteredUnassigned.length - 1 ? '1px solid #e9ecef' : 'none' }}
                  >
                    <div>
                      <span style={{ fontWeight: 500 }}>{p.firstName} {p.lastName}</span>
                      {p.title && (
                        <span className="text-muted ms-2" style={{ fontSize: '0.85rem' }}>
                          ({p.title})
                        </span>
                      )}
                    </div>
                    <Button
                      color="success"
                      size="sm"
                      onClick={() => handleAssign(p.id)}
                      disabled={adding === p.id}
                    >
                      {adding === p.id ? <Spinner size="sm" /> : <i className="ri-add-line"></i>}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Summary by Title */}
            {projectPersonnel.length > 0 && (
              <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }} className="rounded-3 p-3 mb-3">
                <div className="text-muted small mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Görev Özeti
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {Object.entries(personnelByTitle).map(([title, count]) => (
                    <div key={title} className="px-3 py-2 rounded-3" style={{ fontSize: '0.85rem', background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
                      <i className="ri-user-line text-warning me-1"></i>
                      <span style={{ fontWeight: 500 }}>{count}</span>
                      <span className="ms-1">{title}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Personnel List */}
            <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #405189' }} className="rounded-3 p-3 mb-3">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <span className="text-muted small" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Proje Personeli
                </span>
                <span className="text-muted small">{projectPersonnel.length} kişi</span>
              </div>
              {projectPersonnel.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted mb-3">Bu projeye henüz personel atanmamış</p>
                  {isAdmin && (
                    <Button color="primary" size="sm" onClick={() => setShowAddPanel(true)}>
                      <i className="ri-user-add-line me-1"></i>
                      Personel Ekle
                    </Button>
                  )}
                </div>
              ) : (
                <div style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }} className="rounded-3 overflow-hidden">
                  {projectPersonnel.map((pp, idx) => (
                    <div
                      key={pp.id}
                      className="px-3 py-3"
                      style={{ borderBottom: idx < projectPersonnel.length - 1 ? '1px solid #e9ecef' : 'none' }}
                    >
                      <div className="d-flex align-items-start justify-content-between">
                        <div style={{ flex: 1 }}>
                          <div className="d-flex align-items-center mb-1">
                            <span style={{ fontWeight: 500 }}>
                              {pp.personnel.firstName} {pp.personnel.lastName}
                            </span>
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                            <span className="me-3">
                              <i className="ri-briefcase-line me-1"></i>
                              {pp.personnel.title || 'Görev belirtilmedi'}
                            </span>
                            {pp.personnel.phone && (
                              <span>
                                <i className="ri-phone-line me-1"></i>
                                {pp.personnel.phone}
                              </span>
                            )}
                          </div>
                        </div>
                        {isAdmin && (
                          <Button
                            color="danger"
                            size="sm"
                            outline
                            onClick={() => handleRemove(pp.personnel.id)}
                            disabled={removing === pp.personnel.id}
                            style={{ padding: '0.15rem 0.4rem' }}
                          >
                            {removing === pp.personnel.id ? (
                              <Spinner size="sm" />
                            ) : (
                              <i className="ri-close-line" style={{ fontSize: '0.9rem' }}></i>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Info Note */}
            <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #299cdb' }} className="rounded-3 p-3 mb-3">
              <div className="d-flex align-items-start">
                <i className="ri-information-line text-info me-2" style={{ fontSize: '1.1rem' }}></i>
                <small className="text-muted">
                  Personeller önce projeye atanır, sonra projedeki personeller şantiyelere dağıtılabilir.
                </small>
              </div>
            </div>
          </>
        )}
      </div>
    </SideModal>
  );
};

export default ProjectPersonnelModal;
